import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Loader } from '@mantine/core';
import { useReviews, useSellerReply, type Review } from '@modules/reviews';
import { useProducts, productThumbSrc } from '@modules/products';
import { Breadcrumbs, OptionsTable, type OptionsTableColumn } from '@components';
import { Modal, CachedImage, RefetchButton, Textarea, Button, DoubleCheckModal, Pagination, QuietSelect } from '@shared/ui';
import { IoImageOutline, IoStar } from 'react-icons/io5';
import { formatDate, formatPhoneNumber } from '@shared/utils';
import { storageFileUrl } from '@shared/utils/storage-file-url';
import '@shared/styles/page.css';
import './ReviewsPage.css';

const LIKED_FEATURE_LABELS: Record<string, string> = {
  AS_ON_PICTURE: 'Как на картинке',
  SMELL: 'Запах',
  PACKAGING: 'Упаковка',
  APPEARANCE: 'Внешний вид',
};

const SELLER_REPLY_MAX = 1000;
const REVIEWS_PAGE_SIZE = 25;

function reviewAuthorName(review: Review): string {
  const u = review.user;
  if (!u) return 'Покупатель';
  return [u.name, u.surname].filter(Boolean).join(' ') || 'Покупатель';
}

function sellerReplyStatusLabel(approved: boolean | null): string {
  if (approved === true) return 'Опубликован';
  if (approved === false) return 'Отклонён';
  return 'На модерации';
}

export default function ReviewsPage() {
  const [page, setPage] = useState(1);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const filterKey = selectedProductId == null ? '' : String(selectedProductId);
  const [pageFilterKey, setPageFilterKey] = useState(filterKey);
  if (pageFilterKey !== filterKey) {
    setPageFilterKey(filterKey);
    setPage(1);
  }
  const currentPage = pageFilterKey === filterKey ? page : 1;
  const { data: reviewsPage, isLoading, error, refetch, isFetching } = useReviews({
    page: currentPage,
    limit: REVIEWS_PAGE_SIZE,
    productId: selectedProductId ?? undefined,
  });
  const reviews = reviewsPage?.items ?? [];
  const reviewsTotal = reviewsPage?.total ?? 0;
  const { data: products = [] } = useProducts();
  const sellerReply = useSellerReply();
  const [reviewModal, setReviewModal] = useState<Review | null>(null);
  const [replyDraft, setReplyDraft] = useState('');
  const [replyConfirmOpen, setReplyConfirmOpen] = useState(false);
  const [productSearchQuery, setProductSearchQuery] = useState('');

  const openReviewModal = (review: Review) => {
    setReviewModal(review);
    // После отклонения форма пустая — отклонённый текст показываем отдельным блоком
    setReplyDraft('');
    setReplyConfirmOpen(false);
  };

  const closeReviewModal = () => {
    setReviewModal(null);
    setReplyDraft('');
    setReplyConfirmOpen(false);
  };

  const confirmSellerReply = () => {
    if (!reviewModal) return;
    const text = replyDraft.trim();
    if (!text) return;
    sellerReply.mutate(
      { id: reviewModal.id, text },
      {
        onSuccess: (updated) => {
          setReviewModal(updated);
          setReplyDraft('');
          setReplyConfirmOpen(false);
        },
      },
    );
  };

  const canEditSellerReply =
    !reviewModal?.sellerReply || reviewModal.sellerReplyApproved === false;
  const showReadonlySellerReply =
    Boolean(reviewModal?.sellerReply) &&
    reviewModal?.sellerReplyApproved !== false;

  const productOptions = useMemo(
    () =>
      products.map((product) => {
        const categoryNames =
          product.categories
            ?.map((cat) => cat.category.name)
            .filter(Boolean)
            .join(', ') || '';
        const label = categoryNames ? `${product.name} (${categoryNames})` : product.name;
        return { id: product.id, label };
      }),
    [products],
  );

  const filteredProductOptions = useMemo(() => {
    const q = productSearchQuery.trim().toLowerCase();
    if (!q) return productOptions;
    return productOptions.filter((p) => p.label.toLowerCase().includes(q));
  }, [productOptions, productSearchQuery]);

  const selectedProductLabel = useMemo(() => {
    if (selectedProductId == null) return 'Все товары';
    return productOptions.find((p) => p.id === selectedProductId)?.label ?? 'Все товары';
  }, [productOptions, selectedProductId]);

  const listLoading = isLoading || isFetching;

  const reviewColumns: OptionsTableColumn<Review>[] = [
    {
      key: 'id',
      label: 'ID',
      style: { width: '60px' },
    },
    {
      key: 'rating',
      label: 'Оценка',
      render: (review: Review) => (
        <span className="reviews-page-rating">
          {review.rating}
          <IoStar size={14} className="reviews-page-rating-star" aria-hidden />
        </span>
      ),
    },
    {
      key: 'text',
      label: 'Текст',
      render: (review: Review) => {
        const text = review.text || '—';
        const hasPhotos = review.photos?.length > 0;
        const hasReply = Boolean(review.sellerReply);
        return (
          <span className="reviews-page-text-cell">
            {hasPhotos ? (
              <IoImageOutline size={15} className="reviews-page-text-cell-icon" title="Есть фото" />
            ) : null}
            <span className="reviews-page-text-cell-text">{text}</span>
            {hasReply ? (
              <span
                className={`reviews-page-reply-badge${review.sellerReplyApproved === true
                    ? ' reviews-page-reply-badge--ok'
                    : review.sellerReplyApproved === false
                      ? ' reviews-page-reply-badge--rejected'
                      : ' reviews-page-reply-badge--pending'
                  }`}
                title={sellerReplyStatusLabel(review.sellerReplyApproved)}
              >
                ответ
              </span>
            ) : null}
          </span>
        );
      },
    },
    {
      key: 'product',
      label: 'Товар',
      render: (review: Review) => {
        const name = review.product?.name ?? '—';
        if (review.productId) {
          return (
            <Link
              to={`/products/${review.productId}`}
              state={{ from: 'reviews' }}
              className="reviews-page-link"
              onClick={(e) => e.stopPropagation()}
            >
              {name}
            </Link>
          );
        }
        return name;
      },
    },
    {
      key: 'order',
      label: 'Заказ',
      render: (review: Review) => {
        const orderNumber = review.order?.orderNumber ?? '—';
        if (review.orderId) {
          return (
            <Link
              to={`/orders/${review.orderId}`}
              state={{ from: 'reviews' }}
              className="reviews-page-link"
              onClick={(e) => e.stopPropagation()}
            >
              {orderNumber}
            </Link>
          );
        }
        return orderNumber;
      },
    },
    {
      key: 'user',
      label: 'Пользователь',
      render: (review: Review) => {
        const u = review.user;
        if (!u) return '—';
        const name = [u.name, u.surname].filter(Boolean).join(' ') || '—';
        return (
          <span title={formatPhoneNumber(u.phone)}>
            {name}
            <br />
            <small style={{ opacity: 0.8 }}>{formatPhoneNumber(u.phone)}</small>
          </span>
        );
      },
    },
    {
      key: 'createdAt',
      label: 'Дата',
      render: (review: Review) => formatDate(review.createdAt),
    },
  ];

  return (
    <div className="page-container reviews-page">
      <Breadcrumbs
        items={[
          { label: 'Главная', path: '/' },
          { label: 'Отзывы' },
        ]}
      />

      <div className="page-header">
        <div className="page-header-top reviews-page-header-top">
          <h1>Отзывы</h1>
          <QuietSelect<number | 'all'>
            className="reviews-page__select-wrap"
            ariaLabel="Фильтр по товару"
            label={selectedProductLabel}
            value={selectedProductId ?? 'all'}
            search={{
              value: productSearchQuery,
              onChange: setProductSearchQuery,
              placeholder: 'Поиск товара...',
            }}
            onChange={(next) => {
              setSelectedProductId(next === 'all' ? null : next);
            }}
            options={[
              ...(!productSearchQuery.trim()
                ? [{ value: 'all' as const, label: 'Все товары' }]
                : []),
              ...filteredProductOptions.map((product) => ({
                value: product.id,
                label: product.label,
              })),
            ]}
          />
        </div>
      </div>

      <div className="content-wrapper reviews-page-content-wrapper">
        <div className="reviews-table-scroll">
          <div className="reviews-desktop-table">
            <OptionsTable
              columns={reviewColumns}
              data={reviews}
              loading={listLoading}
              error={error}
              refetch={refetch}
              emptyMessage="Нет отзывов"
              getActions={() => []}
              onRowClick={(review) => openReviewModal(review)}
            />
          </div>

          <div className="reviews-mobile-list" aria-label="Список отзывов">
            {error && refetch ? (
              <RefetchButton
                refetch={refetch}
                isLoading={listLoading}
                errorMessage={
                  error instanceof Error ? error.message : 'Не удалось загрузить данные'
                }
              />
            ) : listLoading ? (
              <div className="empty-container">
                <Loader />
              </div>
            ) : reviews.length === 0 ? (
              <div className="reviews-page__empty" role="status">
                Нет отзывов
              </div>
            ) : (
              reviews.map((review) => {
                const productName = review.product?.name ?? 'Товар';
                const hasPhotos = (review.photos?.length ?? 0) > 0;
                return (
                  <button
                    key={review.id}
                    type="button"
                    className="reviews-mobile-card"
                    onClick={() => openReviewModal(review)}
                  >
                    <div className="reviews-mobile-card__top">
                      <span
                        className="reviews-mobile-card__rating"
                        aria-label={`Оценка ${review.rating} из 5`}
                      >
                        {Array.from({ length: 5 }, (_, i) => (
                          <IoStar
                            key={i}
                            size={14}
                            className={
                              i < review.rating
                                ? 'reviews-page-modal__star reviews-page-modal__star--on'
                                : 'reviews-page-modal__star'
                            }
                            aria-hidden
                          />
                        ))}
                        <span className="reviews-mobile-card__rating-num">{review.rating}</span>
                      </span>
                      <span className="reviews-mobile-card__date">
                        {formatDate(review.createdAt)}
                      </span>
                    </div>

                    <div className="reviews-mobile-card__product">
                      {productName}
                      {hasPhotos ? (
                        <IoImageOutline
                          size={14}
                          className="reviews-mobile-card__photo-icon"
                          aria-label="Есть фото"
                        />
                      ) : null}
                      {review.sellerReply ? (
                        <span className="reviews-mobile-card__reply-mark">ответ</span>
                      ) : null}
                    </div>

                    <p className="reviews-mobile-card__text">
                      {review.text?.trim() || 'Без текста'}
                    </p>

                    <div className="reviews-mobile-card__footer">
                      <span className="reviews-mobile-card__author">{reviewAuthorName(review)}</span>
                      {review.orderId ? (
                        <span className="reviews-mobile-card__order">
                          Заказ {review.order?.orderNumber ?? review.orderId}
                        </span>
                      ) : null}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
        <Pagination
          page={currentPage}
          onChange={setPage}
          totalItems={reviewsTotal}
          pageSize={REVIEWS_PAGE_SIZE}
        />
      </div>

      <Modal
        isOpen={reviewModal !== null}
        onClose={closeReviewModal}
        title="Отзыв"
        maxWidth={520}
      >
        {reviewModal && (
          <div className="reviews-page-modal">
            <Link
              to={`/products/${reviewModal.productId}`}
              state={{ from: 'reviews' }}
              className="reviews-page-modal__product"
              onClick={closeReviewModal}
            >
              {productThumbSrc(reviewModal.product?.images?.[0]) ? (
                <div className="reviews-page-modal__product-image">
                  <CachedImage
                    src={productThumbSrc(reviewModal.product.images?.[0]) || ''}
                    alt={reviewModal.product?.name ?? 'Товар'}
                    loading="lazy"
                  />
                </div>
              ) : (
                <div
                  className="reviews-page-modal__product-image reviews-page-modal__product-image--empty"
                  aria-hidden
                />
              )}
              <div className="reviews-page-modal__product-info">
                <span className="reviews-page-modal__product-name">
                  {reviewModal.product?.name ?? 'Товар'}
                </span>
                <span className="reviews-page-modal__product-hint">Открыть карточку товара</span>
              </div>
            </Link>

            <div className="reviews-page-modal__meta">
              <span
                className="reviews-page-modal__rating"
                aria-label={`Оценка ${reviewModal.rating} из 5`}
              >
                {Array.from({ length: 5 }, (_, i) => (
                  <IoStar
                    key={i}
                    size={16}
                    className={
                      i < reviewModal.rating
                        ? 'reviews-page-modal__star reviews-page-modal__star--on'
                        : 'reviews-page-modal__star'
                    }
                    aria-hidden
                  />
                ))}
                <span className="reviews-page-modal__rating-num">{reviewModal.rating}</span>
              </span>
              <span className="reviews-page-modal__meta-sep" aria-hidden>
                ·
              </span>
              <span className="reviews-page-modal__meta-text">
                {formatDate(reviewModal.createdAt)}
              </span>
              {reviewModal.orderId ? (
                <>
                  <span className="reviews-page-modal__meta-sep" aria-hidden>
                    ·
                  </span>
                  <Link
                    to={`/orders/${reviewModal.orderId}`}
                    state={{ from: 'reviews' }}
                    className="reviews-page-modal__order-link"
                    onClick={closeReviewModal}
                  >
                    Заказ {reviewModal.order?.orderNumber ?? reviewModal.orderId}
                  </Link>
                </>
              ) : null}
            </div>

            <div className="reviews-page-modal__author">
              <span className="reviews-page-modal__author-name">{reviewAuthorName(reviewModal)}</span>
              {reviewModal.user?.phone ? (
                <span className="reviews-page-modal__author-phone">
                  {formatPhoneNumber(reviewModal.user.phone)}
                </span>
              ) : null}
            </div>

            {reviewModal.likedFeatures?.length > 0 ? (
              <div className="reviews-page-modal__tags">
                {reviewModal.likedFeatures.map((feature) => (
                  <span key={feature} className="reviews-page-modal__tag">
                    {LIKED_FEATURE_LABELS[feature] ?? feature}
                  </span>
                ))}
              </div>
            ) : null}

            <div className="reviews-page-modal__text">
              {reviewModal.text?.trim() || 'Без текста'}
            </div>

            {reviewModal.photos && reviewModal.photos.length > 0 ? (
              <div className="reviews-page-modal__photos">
                <div className="reviews-page-modal__photos-title">Фото от покупателя</div>
                <div className="reviews-page-modal__photos-grid">
                  {reviewModal.photos.map((url) => (
                    <a
                      key={url}
                      href={storageFileUrl(url) || url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="reviews-page-modal__photo"
                    >
                      <CachedImage src={url} alt="" loading="lazy" />
                    </a>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="reviews-page-modal__seller-reply">
              <div className="reviews-page-modal__seller-reply-title">Ответ магазина</div>
              {showReadonlySellerReply ? (
                <>
                  <div
                    className={`reviews-page-modal__seller-reply-status${
                      reviewModal.sellerReplyApproved === true
                        ? ' reviews-page-modal__seller-reply-status--ok'
                        : ' reviews-page-modal__seller-reply-status--pending'
                    }`}
                  >
                    {sellerReplyStatusLabel(reviewModal.sellerReplyApproved)}
                    {reviewModal.sellerRepliedAt
                      ? ` · ${formatDate(reviewModal.sellerRepliedAt)}`
                      : ''}
                  </div>
                  <div className="reviews-page-modal__seller-reply-text">
                    {reviewModal.sellerReply}
                  </div>
                </>
              ) : (
                <>
                  {reviewModal.sellerReplyApproved === false && reviewModal.sellerReply ? (
                    <>
                      <div className="reviews-page-modal__seller-reply-status reviews-page-modal__seller-reply-status--rejected">
                        Отклонён
                        {reviewModal.sellerRepliedAt
                          ? ` · ${formatDate(reviewModal.sellerRepliedAt)}`
                          : ''}
                      </div>
                      <div className="reviews-page-modal__seller-reply-text reviews-page-modal__seller-reply-text--rejected">
                        {reviewModal.sellerReply}
                      </div>
                      <div className="reviews-page-modal__seller-reply-title">
                        Новый ответ
                      </div>
                    </>
                  ) : null}
                  <Textarea
                    placeholder="Напишите ответ покупателю…"
                    value={replyDraft}
                    onChange={(e) => setReplyDraft(e.target.value.slice(0, SELLER_REPLY_MAX))}
                    minRows={3}
                    maxRows={8}
                    autosize
                  />
                  <div className="reviews-page-modal__seller-reply-footer">
                    <span className="reviews-page-modal__seller-reply-hint">
                      {reviewModal.sellerReplyApproved === false
                        ? 'Измените текст и отправьте снова — ответ пройдёт модерацию.'
                        : 'После отправки ответ нельзя будет изменить, пока он на модерации или опубликован.'}
                    </span>
                    <span className="reviews-page-modal__seller-reply-counter">
                      {replyDraft.trim().length}/{SELLER_REPLY_MAX}
                    </span>
                  </div>
                  <Button
                    type="button"
                    disabled={!replyDraft.trim() || sellerReply.isPending || !canEditSellerReply}
                    loading={sellerReply.isPending}
                    onClick={() => setReplyConfirmOpen(true)}
                  >
                    {reviewModal.sellerReplyApproved === false
                      ? 'Отправить снова'
                      : 'Ответить'}
                  </Button>
                </>
              )}
            </div>

            {reviewModal.adminReply ? (
              <div className="reviews-page-modal__seller-reply">
                <div className="reviews-page-modal__seller-reply-title">Лавка цветов</div>
                <div className="reviews-page-modal__seller-reply-status reviews-page-modal__seller-reply-status--ok">
                  Опубликован
                  {reviewModal.adminRepliedAt
                    ? ` · ${formatDate(reviewModal.adminRepliedAt)}`
                    : ''}
                </div>
                <div className="reviews-page-modal__seller-reply-text">
                  {reviewModal.adminReply}
                </div>
              </div>
            ) : null}
          </div>
        )}
      </Modal>

      <DoubleCheckModal
        isOpen={replyConfirmOpen}
        onClose={() => setReplyConfirmOpen(false)}
        title={
          reviewModal?.sellerReplyApproved === false
            ? 'Отправить ответ снова?'
            : 'Отправить ответ?'
        }
        message={
          reviewModal?.sellerReplyApproved === false
            ? 'Новый текст уйдёт на модерацию. Пока ответ не одобрен или не отклонён, изменить его нельзя.'
            : 'После отправки ответ нельзя будет изменить, пока он на модерации или опубликован.'
        }
        firstButtonText="Отмена"
        secondButtonText="Отправить"
        secondButtonAction={confirmSellerReply}
        isLoading={sellerReply.isPending}
      />
    </div>
  );
}
